import { JSX, ReactNode } from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectArrayInput,
    BooleanInput,
    useRecordContext,
    WithRecord,
    usePermissions,
    Toolbar,
    SaveButton,
    DeleteButton,
} from 'react-admin';
import { Alert, Box, Chip, Divider, Typography } from "@mui/material";
import { permissionManager } from "../permissions/manager";
import { SystemEmailTemplateCodeEditor } from "../code-editor/SystemEmailTemplateCodeEditor";
import { SystemDefaultHTMLTemplateCodeEditor } from "../code-editor/SystemDefaultHTMLTemplateCodeEditor";
import { Setting } from "../../types/entities";
import { KeyValueEditor } from "./components/settings/KeyValueEditor";

// Matches the settings index, so a setting's code reads the same in both places.
const MONO: string = '"Source Code Pro Regular", ui-monospace, SFMono-Regular, Menlo, monospace';

const FormToolbar: () => JSX.Element = (): JSX.Element => {
    const { permissions } = usePermissions();
    const record: Setting = useRecordContext();

    // Migrated to the ACL 2026-09-10 (Stage 4a). Deleting a system setting is its own resource,
    // because it is a narrower act than editing an ordinary one.
    //
    // This used to be the *only* place that rule existed: HQ-API's Delete expression made no
    // distinction, so any super admin could remove a system setting through the API while the
    // button was hidden from them here. The Delete expression on `Setting` now carries the same
    // condition, so the screen and the API finally agree.
    // Per-verb permissions changed the second branch and not the first. Deleting an ordinary
    // setting is `settings.delete`; `HQ_Admin::settings_system` stays a resource of its own
    // because it says which *records* may be deleted, not which operation may be performed, and
    // the Delete expression on `Setting` asks for both.
    const canDelete: boolean = record?.system
        ? permissionManager.isAllowed(permissions, 'HQ_Admin::settings_system')
            && permissionManager.isAllowed(permissions, 'HQ_Admin::settings.delete')
        : permissionManager.isAllowed(permissions, 'HQ_Admin::settings.delete');

    return (
        <Toolbar>
            <SaveButton label="Save" />
            { canDelete &&
                <DeleteButton sx={ { marginLeft: 'auto' } } />
            }
        </Toolbar>
    );
}

/**
 * The frame every setting is edited in.
 *
 * Each of the seven type branches used to repeat this header itself, with its
 * own inline font sizes and margins, so they had drifted apart. The branches
 * below now return only their input.
 */
// Code editors and repeatable rows need the room; a single text field reads
// better in a measured column.
const WIDE_TYPES: string[] = [
    'system_email_template',
    'system_invoice_template',
    'key/value',
    'textarea',
];

const SettingFrame: (props: { setting: Setting, children: ReactNode }) => JSX.Element =
    ({ setting, children }: { setting: Setting, children: ReactNode }): JSX.Element => (
        <Box sx={{ width: '100%', maxWidth: WIDE_TYPES.includes(setting.type) ? '100%' : 900 }}>
            <Typography component="h1" sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.25 }}>
                { setting.label }
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                    size="small"
                    label={ setting.code }
                    sx={{
                        fontFamily: MONO,
                        fontSize: 11,
                        height: 22,
                        borderRadius: 1,
                    }}
                />
                { setting.system &&
                    <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label="System"
                        title="System settings can only be deleted by an owner"
                        sx={{ fontSize: 11, height: 22, borderRadius: 1 }}
                    />
                }
            </Box>

            <Divider sx={{ my: 2.5 }} />

            <Box sx={{ width: '100%' }}>
                { children }
            </Box>

            { (setting.comment !== undefined && setting.comment !== '') &&
                <Alert severity="info" variant="outlined" sx={{ mt: 2.5, fontSize: 13 }}>
                    { setting.comment }
                </Alert>
            }
        </Box>
    );

/** The input for a setting, chosen by its type. */
const SettingInput: (props: { setting: Setting }) => JSX.Element =
    ({ setting }: { setting: Setting }): JSX.Element => {
        switch (setting.type) {

            case 'text':
                return <TextInput label={ false } name="value" source="value" fullWidth />;

            case 'textarea':
                return (
                    <TextInput
                        label={ false }
                        name="value"
                        source="value"
                        multiline
                        minRows={ 10 }
                        fullWidth />
                );

            // 'multiple' is what the create form writes for this type; both are
            // accepted so a setting created there is still editable here.
            case 'multi':
            case 'multiple': {
                const choices: { id: string, name: string }[] = [];
                if (Array.isArray(setting.value)) {
                    (setting.value as string[]).forEach((value: string): void => {
                        if (!choices.some((choice): boolean => choice.id === value)) {
                            choices.push({ id: value, name: value });
                        }
                    });
                }
                return (
                    <SelectArrayInput
                        name="value"
                        source="value"
                        label={ false }
                        fullWidth
                        choices={ choices }
                        onCreate={ (): { id: string, name: string } => {
                            const entered: string | null = prompt("Enter new choice");
                            if (entered === null || entered.trim() === '') {
                                return undefined;
                            }
                            const newChoice = {
                                id: entered.replaceAll(' ', '_').toLowerCase(),
                                name: entered.replaceAll(' ', '_').toLowerCase(),
                            };
                            choices.push(newChoice);
                            return newChoice;
                        } }
                        createLabel="Create new option..."
                    />
                );
            }

            case 'yes/no':
                return <BooleanInput source="value" name="value" label={ false } />;

            case 'system_email_template':
                return <SystemEmailTemplateCodeEditor />;

            case 'system_invoice_template':
                return <SystemDefaultHTMLTemplateCodeEditor />;

            case 'key/value':
                return <KeyValueEditor fileName={ setting.code || 'setting' } />;
        }

        return (
            <Alert severity="warning" variant="outlined" sx={{ fontSize: 13 }}>
                { `This setting has the type “${setting.type}”, which has no editor yet.` }
            </Alert>
        );
    };

export const SettingsEdit: () => JSX.Element = (): JSX.Element => (
    <Edit redirect={ false } title="Edit Setting">
        <SimpleForm toolbar={ <FormToolbar /> }>
            <WithRecord label="" render={ (record: Setting): JSX.Element => (
                <SettingFrame setting={ record }>
                    <SettingInput setting={ record } />
                </SettingFrame>
            ) } />
        </SimpleForm>
    </Edit>
);
